import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  FileText, Plus, Edit3, ChevronDown, Trash2,
  FileCheck2, GitBranch, ClipboardList, Filter, ExternalLink,
  ShieldCheck, X,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import PhotoUploader from "@/components/PhotoUploader";
import { formatDate } from "@/lib/format";

const DEFAULT_PILLARS = ["Quality", "Food Safety / HACCP", "Maintenance", "Sanitation",
                 "HR / People", "Environmental / Sustainability", "Safety", "Operations", "Other"];
const DEFAULT_CODE_PREFIXES = [
  ["SOP", "SOP (Standard Operating Procedure)"],
  ["WI", "WI (Work Instruction)"],
  ["POL", "POL (Policy)"],
  ["FRM", "FRM (Form)"],
  ["RCD", "RCD (Record)"],
  ["MAN", "MAN (Manual)"],
];

export default function SopsAndDocs() {
  const [tab, setTab] = useState("library"); // library | approvals | changelog | deleted
  const [role, setRole] = useState(null);
  const [pillars, setPillars] = useState(DEFAULT_PILLARS);
  const [codePrefixes, setCodePrefixes] = useState(DEFAULT_CODE_PREFIXES);

  useEffect(() => {
    api.get("/orgs/me").then((r) => {
      setRole(r.data.role);
      const org = r.data.org || {};
      if (org.pillars && org.pillars.length) setPillars(org.pillars);
      if (org.code_prefixes && org.code_prefixes.length) {
        setCodePrefixes(org.code_prefixes.map((p) => [p.code, p.label || p.code]));
      }
    }).catch(() => {});
  }, []);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Document Control</div>
          <h1 className="font-display font-black text-3xl">Doc Library</h1>
        </div>
        <TabBar tab={tab} setTab={setTab} isController={role === "controller"} />
      </div>

      {tab === "library" && <LibraryTab role={role} pillars={pillars} codePrefixes={codePrefixes} />}
      {tab === "approvals" && role === "controller" && <ApprovalsTab />}
      {tab === "changelog" && <ChangelogTab />}
      {tab === "deleted" && <DeletedBinTab role={role} />}
    </div>
  );
}

function TabBar({ tab, setTab, isController }) {
  const tabs = [
    ["library", "Library", FileText],
    ...(isController ? [["approvals", "Approvals", ShieldCheck]] : []),
    ["changelog", "Changelog", ClipboardList],
    ...(isController ? [["deleted", "Deleted", Trash2]] : []),  // iter55
  ];
  return (
    <div className="flex flex-wrap gap-1 bg-[#f7f5f2] rounded-full p-1" data-testid="sop-tabs">
      {tabs.map(([k, l, Icon]) => (
        <button key={k} data-testid={`sop-tab-${k}`} onClick={() => setTab(k)}
          className={`px-3 py-1.5 rounded-full text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5 ${
            tab === k ? "bg-[#1a1a1a] text-white" : "text-[#1a1a1a]/60 hover:text-[#1a1a1a]"
          }`}>
          <Icon className="w-3 h-3" /> {l}
        </button>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// LIBRARY TAB — with inline RACI (R Responsible, A Accountable, C Consulted, I Informed)
// ═══════════════════════════════════════════════════════════════════════
function LibraryTab({ role, pillars, codePrefixes }) {
  const [docs, setDocs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [pillar, setPillar] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());   // iter57 — bulk RACI selection
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = () => api.get("/documents").then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/employees").then((r) => setEmployees(r.data || [])).catch(() => setEmployees([]));
  }, []);

  const empName = (id) => employees.find((e) => e.id === id)?.name || "—";
  const empNames = (ids) => (ids || []).map((id) => employees.find((e) => e.id === id)?.name).filter(Boolean);
  const visible = useMemo(() => pillar === "all" ? docs : docs.filter((d) => d.pillar === pillar), [docs, pillar]);

  // iter56 — clicking the Doc ID opens the actual file (read). Falls back to editor if no file.
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const token = localStorage.getItem("opscmd_token") || "";
  const fileUrl = (fid) => `${backend}/api/files/${fid}?auth=${encodeURIComponent(token)}`;
  const openDoc = (d) => {
    if (d.current_file_id) window.open(fileUrl(d.current_file_id), "_blank", "noopener,noreferrer");
    else { toast.info("No approved file uploaded yet — opening editor."); setEditTarget(d); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="w-3 h-3 text-[#1a1a1a]/50" />
        <select data-testid="pillar-filter" value={pillar} onChange={(e) => setPillar(e.target.value)}
          className="border border-[#1a1a1a]/15 rounded-lg px-2 py-1 text-[12px] bg-white">
          <option value="all">All pillars</option>
          {pillars.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <div className="flex-1" />
        {selectedIds.size > 0 && (role === "admin" || role === "controller") && (
          <button
            data-testid="bulk-raci-btn"
            onClick={() => setBulkOpen(true)}
            className="btn-pill btn-pill-secondary text-[11px]"
          >
            Bulk RACI ({selectedIds.size})
          </button>
        )}
        {(role === "admin" || role === "controller") && (
          <button data-testid="sop-create-btn" onClick={() => setCreateOpen(true)} className="btn-pill btn-pill-primary text-[11px]">
            <Plus className="w-3 h-3" /> New document
          </button>
        )}
      </div>

      <div className="surface-card rounded-2xl overflow-hidden">
        <table className="w-full text-[12px] table-fixed" data-testid="doc-library-table">
          <colgroup>
            <col className="w-[30px]" />
            <col className="w-[12%]" />
            <col />
            <col className="w-[9%]" />
            <col className="w-[5%]" />
            <col className="w-[10%]" />
            <col className="w-[10%]" />
            <col className="w-[12%]" />
            <col className="w-[12%]" />
            <col className="w-[7%]" />
            <col className="w-[8%]" />
            <col className="w-[36px]" />
          </colgroup>
          <thead className="bg-[#1a1a1a] text-white">
            <tr>
              <th className="px-1 py-2">
                {(role === "admin" || role === "controller") && (
                  <input
                    type="checkbox"
                    data-testid="bulk-select-all"
                    checked={visible.length > 0 && visible.every((d) => selectedIds.has(d.id))}
                    onChange={(e) => {
                      const next = new Set(selectedIds);
                      if (e.target.checked) visible.forEach((d) => next.add(d.id));
                      else visible.forEach((d) => next.delete(d.id));
                      setSelectedIds(next);
                    }}
                    className="cursor-pointer"
                  />
                )}
              </th>
              <th className="text-left px-2 py-2 text-[9px] uppercase tracking-wider font-mono font-bold">Doc ID</th>
              <th className="text-left px-2 py-2 text-[9px] uppercase tracking-wider font-mono font-bold">Title</th>
              <th className="text-left px-2 py-2 text-[9px] uppercase tracking-wider font-mono font-bold">Pillar</th>
              <th className="text-left px-2 py-2 text-[9px] uppercase tracking-wider font-mono font-bold">Rev</th>
              <th className="text-left px-2 py-2 text-[9px] uppercase tracking-wider font-mono font-bold">R · Resp</th>
              <th className="text-left px-2 py-2 text-[9px] uppercase tracking-wider font-mono font-bold">A · Acct</th>
              <th className="text-left px-2 py-2 text-[9px] uppercase tracking-wider font-mono font-bold">C · Consulted</th>
              <th className="text-left px-2 py-2 text-[9px] uppercase tracking-wider font-mono font-bold">I · Informed</th>
              <th className="text-left px-2 py-2 text-[9px] uppercase tracking-wider font-mono font-bold">Status</th>
              <th className="text-left px-2 py-2 text-[9px] uppercase tracking-wider font-mono font-bold">Review</th>
              <th />
            </tr>
          </thead>
          <tbody className="divide-y divide-[#1a1a1a]/06">
            {visible.length === 0 ? (
              <tr><td colSpan={12} className="p-8 text-center text-[12px] text-[#1a1a1a]/55 font-mono uppercase tracking-wider">No documents yet.</td></tr>
            ) : visible.map((d) => (
              <tr key={d.id} data-testid={`doc-row-${d.id}`} className="hover:bg-[#f7f5f2]/50 align-top">
                <td className="px-1 py-2.5 text-center">
                  {(role === "admin" || role === "controller") && (
                    <input
                      type="checkbox"
                      data-testid={`bulk-select-${d.id}`}
                      checked={selectedIds.has(d.id)}
                      onChange={(e) => {
                        const next = new Set(selectedIds);
                        if (e.target.checked) next.add(d.id); else next.delete(d.id);
                        setSelectedIds(next);
                      }}
                      className="cursor-pointer"
                    />
                  )}
                </td>
                <td className="px-2 py-2.5">
                  <button
                    onClick={() => openDoc(d)}
                    data-testid={`doc-code-${d.id}`}
                    title={d.current_file_id ? "Open document to read" : "No file uploaded — opens editor"}
                    className="font-mono font-bold text-[#ff4500] hover:underline text-left break-all inline-flex items-center gap-1"
                  >
                    <span>{d.code}</span>
                    {d.current_file_id && <ExternalLink className="w-3 h-3 opacity-60 flex-shrink-0" />}
                  </button>
                </td>
                <td className="px-2 py-2.5 font-medium break-words">{d.title}</td>
                <td className="px-2 py-2.5 font-mono text-[10px] uppercase tracking-wider text-[#1a5fb4] break-words">{d.pillar}</td>
                <td className="px-2 py-2.5 font-mono font-bold">{d.current_rev || "—"}</td>
                <td className="px-2 py-2.5 text-[#1a1a1a]/80 text-[11px] break-words">{empName(d.raci_r_id || d.owner_id)}</td>
                <td className="px-2 py-2.5 text-[#1a1a1a]/80 text-[11px] break-words">{empName(d.raci_a_id)}</td>
                <td className="px-2 py-2.5 text-[#1a1a1a]/70 text-[11px]">
                  {empNames(d.raci_c_ids).length === 0 ? <span className="text-[#1a1a1a]/30">—</span> :
                    <div className="flex flex-wrap gap-1">
                      {empNames(d.raci_c_ids).map((n, i) => <span key={i} className="inline-block bg-[#1a1a1a]/06 rounded-full px-1.5 py-0.5 text-[10px]">{n}</span>)}
                    </div>
                  }
                </td>
                <td className="px-2 py-2.5 text-[#1a1a1a]/70 text-[11px] whitespace-normal break-words">
                  {empNames(d.raci_i_ids).length === 0 ? <span className="text-[#1a1a1a]/30">—</span> :
                    <div className="flex flex-wrap gap-1">
                      {empNames(d.raci_i_ids).map((n, i) => <span key={i} className="inline-block bg-[#1a1a1a]/06 rounded-full px-1.5 py-0.5 text-[10px]">{n}</span>)}
                    </div>
                  }
                </td>
                <td className="px-2 py-2.5"><StatusBadge status={d.status} /></td>
                <td className={`px-2 py-2.5 font-mono text-[10px] ${d.next_review_at && d.next_review_at < new Date().toISOString() ? "text-[#ff4500] font-bold" : "text-[#1a1a1a]/55"}`}>
                  {d.next_review_at ? formatDate(d.next_review_at) : "—"}
                </td>
                <td className="px-1 py-2.5">
                  <button onClick={() => setEditTarget(d)} className="p-1 text-[#1a1a1a]/60 hover:text-[#ff4500]" title="Edit document">
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CreateDocumentDialog open={createOpen} onClose={() => setCreateOpen(false)} onSaved={load} pillars={pillars} codePrefixes={codePrefixes} />
      <DocumentEditorDialog doc={editTarget} role={role} employees={employees} pillars={pillars}
        onClose={() => setEditTarget(null)} onSaved={load} allDocs={docs} />
      <BulkRaciDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onSaved={() => { setSelectedIds(new Set()); load(); }}
        docIds={[...selectedIds]}
        docs={docs}
        employees={employees}
      />
    </div>
  );
}

// iter57 — Bulk RACI Assign — pick multiple docs, set the same R/A/C/I in one shot.
function BulkRaciDialog({ open, onClose, onSaved, docIds, docs, employees }) {
  const [r, setR] = useState("");
  const [a, setA] = useState("");
  const [c, setC] = useState([]);
  const [i, setI] = useState([]);
  const [useR, setUseR] = useState(false);
  const [useA, setUseA] = useState(false);
  const [useC, setUseC] = useState(false);
  const [useI, setUseI] = useState(false);
  const [replaceC, setReplaceC] = useState(true);
  const [replaceI, setReplaceI] = useState(true);
  const [busy, setBusy] = useState(false);

  const selectedDocs = docs.filter((d) => docIds.includes(d.id));

  const submit = async () => {
    if (!useR && !useA && !useC && !useI) return toast.error("Enable at least one field to update");
    setBusy(true);
    try {
      const payload = { document_ids: docIds, replace_c: replaceC, replace_i: replaceI };
      if (useR) payload.raci_r_id = r || "";
      if (useA) payload.raci_a_id = a || "";
      if (useC) payload.raci_c_ids = c;
      if (useI) payload.raci_i_ids = i;
      const res = await api.post("/documents/bulk-raci", payload);
      toast.success(`RACI applied to ${res.data.updated} document(s) · ${res.data.unchanged} unchanged`);
      onSaved?.(); onClose?.();
      setUseR(false); setUseA(false); setUseC(false); setUseI(false);
      setR(""); setA(""); setC([]); setI([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk RACI failed");
    } finally { setBusy(false); }
  };

  const toggleC = (id) => setC(c.includes(id) ? c.filter((x) => x !== id) : [...c, id]);
  const toggleI = (id) => setI(i.includes(id) ? i.filter((x) => x !== id) : [...i, id]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="bulk-raci-dialog">
        <DialogHeader>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500] font-bold">// Bulk RACI Assign</div>
          <DialogTitle className="font-display font-black text-lg">
            Apply RACI to {docIds.length} document{docIds.length === 1 ? "" : "s"}
          </DialogTitle>
        </DialogHeader>

        <div className="bg-[#f7f5f2] rounded-xl p-3 text-[11px] max-h-32 overflow-auto">
          <div className="font-mono text-[10px] uppercase tracking-wider text-[#1a1a1a]/55 mb-1">Selected</div>
          <ul className="space-y-0.5">
            {selectedDocs.map((d) => (
              <li key={d.id}><span className="font-mono font-bold text-[#ff4500]">{d.code}</span> · {d.title}</li>
            ))}
          </ul>
        </div>

        <div className="space-y-4 text-[13px]">
          <p className="text-[12px] text-[#1a1a1a]/70">Tick a field to include it in the bulk update. Untouched fields on each document are preserved. Every change is logged to the changelog.</p>

          <div className="flex items-start gap-3">
            <input type="checkbox" checked={useR} onChange={(e) => setUseR(e.target.checked)} data-testid="bulk-use-r" className="mt-2" />
            <div className="flex-1">
              <label className="block text-[11px] uppercase tracking-[0.18em] font-mono text-[#1a1a1a]/65 mb-1">R · Responsible (single)</label>
              <select disabled={!useR} value={r} onChange={(e) => setR(e.target.value)} className={inp + (useR ? "" : " opacity-40")} data-testid="bulk-r">
                <option value="">— clear —</option>
                {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.name}{emp.position ? ` · ${emp.position}` : ""}</option>)}
              </select>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <input type="checkbox" checked={useA} onChange={(e) => setUseA(e.target.checked)} data-testid="bulk-use-a" className="mt-2" />
            <div className="flex-1">
              <label className="block text-[11px] uppercase tracking-[0.18em] font-mono text-[#1a1a1a]/65 mb-1">A · Accountable (single)</label>
              <select disabled={!useA} value={a} onChange={(e) => setA(e.target.value)} className={inp + (useA ? "" : " opacity-40")} data-testid="bulk-a">
                <option value="">— clear —</option>
                {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.name}{emp.position ? ` · ${emp.position}` : ""}</option>)}
              </select>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <input type="checkbox" checked={useC} onChange={(e) => setUseC(e.target.checked)} data-testid="bulk-use-c" className="mt-2" />
            <div className="flex-1">
              <label className="block text-[11px] uppercase tracking-[0.18em] font-mono text-[#1a1a1a]/65 mb-1">C · Consulted (multi · {c.length} picked)</label>
              <div className={`border border-[#1a1a1a]/15 rounded-xl bg-white max-h-40 overflow-y-auto p-2 ${useC ? "" : "opacity-40 pointer-events-none"}`}>
                {employees.map((emp) => (
                  <label key={emp.id} className="flex items-center gap-2 py-0.5 text-[12px]">
                    <input type="checkbox" checked={c.includes(emp.id)} onChange={() => toggleC(emp.id)} />
                    <span>{emp.name}{emp.position ? ` · ${emp.position}` : ""}</span>
                  </label>
                ))}
              </div>
              <label className="flex items-center gap-2 mt-1 text-[11px] text-[#1a1a1a]/65">
                <input type="checkbox" checked={replaceC} onChange={(e) => setReplaceC(e.target.checked)} disabled={!useC} />
                Replace existing (uncheck to append/merge)
              </label>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <input type="checkbox" checked={useI} onChange={(e) => setUseI(e.target.checked)} data-testid="bulk-use-i" className="mt-2" />
            <div className="flex-1">
              <label className="block text-[11px] uppercase tracking-[0.18em] font-mono text-[#1a1a1a]/65 mb-1">I · Informed (multi · {i.length} picked)</label>
              <div className={`border border-[#1a1a1a]/15 rounded-xl bg-white max-h-40 overflow-y-auto p-2 ${useI ? "" : "opacity-40 pointer-events-none"}`}>
                {employees.map((emp) => (
                  <label key={emp.id} className="flex items-center gap-2 py-0.5 text-[12px]">
                    <input type="checkbox" checked={i.includes(emp.id)} onChange={() => toggleI(emp.id)} />
                    <span>{emp.name}{emp.position ? ` · ${emp.position}` : ""}</span>
                  </label>
                ))}
              </div>
              <label className="flex items-center gap-2 mt-1 text-[11px] text-[#1a1a1a]/65">
                <input type="checkbox" checked={replaceI} onChange={(e) => setReplaceI(e.target.checked)} disabled={!useI} />
                Replace existing (uncheck to append/merge)
              </label>
            </div>
          </div>
        </div>

        <DialogFooter>
          <button onClick={onClose} className="btn-pill btn-pill-secondary text-[11px]">Cancel</button>
          <button data-testid="bulk-raci-submit" onClick={submit} disabled={busy} className="btn-pill btn-pill-primary text-[11px]">
            {busy ? "Applying…" : `Apply to ${docIds.length} doc${docIds.length === 1 ? "" : "s"}`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatusBadge({ status }) {
  const cfg = {
    approved: { label: "Approved", bg: "bg-[#1a8754]/15", fg: "text-[#1a8754]" },
    pending_approval: { label: "Pending", bg: "bg-[#d6a900]/20", fg: "text-[#8f7000]" },
    draft: { label: "Draft", bg: "bg-[#1a1a1a]/8", fg: "text-[#1a1a1a]/70" },
    absorbed: { label: "Absorbed", bg: "bg-[#1a1a1a]/8", fg: "text-[#1a1a1a]/50" },
    retired: { label: "Retired", bg: "bg-[#1a1a1a]/8", fg: "text-[#1a1a1a]/50" },
  }[status] || { label: status || "—", bg: "bg-[#1a1a1a]/5", fg: "text-[#1a1a1a]/60" };
  return (
    <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full ${cfg.bg} ${cfg.fg} inline-block`}>
      {cfg.label}
    </span>
  );
}

// ═══ Create Document Dialog — iter50: field order Doc ID → Title → Pillar → Prefix
function CreateDocumentDialog({ open, onClose, onSaved, pillars, codePrefixes }) {
  const [form, setForm] = useState({ doc_id_hint: "", title: "", pillar: (pillars && pillars[0]) || "Quality", code_prefix: (codePrefixes && codePrefixes[0]?.[0]) || "SOP", review_cycle_days: 365, initial_file_id: "", initial_rev: "A", summary: "" });
  const [fileIds, setFileIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");   // iter56 — inline error banner
  const submit = async () => {
    setErrorMsg("");
    // iter56 — inline validation so the user never has to guess what's wrong
    if (!form.title.trim()) { setErrorMsg("Doc Title is required."); return; }
    if (!(form.summary || "").trim()) { setErrorMsg("Change note / summary is required — it goes into the audit changelog."); return; }
    setBusy(true);
    try {
      const payload = { ...form, initial_file_id: fileIds[0] || null };
      await api.post("/documents", payload);
      toast.success("Document created — awaiting approval in the Approvals tab (Controllers can self-approve).");
      onSaved?.(); onClose?.();
      setForm({ doc_id_hint: "", title: "", pillar: "Quality", code_prefix: "SOP", review_cycle_days: 365, initial_file_id: "", initial_rev: "A", summary: "" });
      setFileIds([]);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Something went wrong saving this document.";
      setErrorMsg(msg);
      toast.error(msg);
    }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg rounded-2xl">
        <DialogHeader><DialogTitle className="font-display font-black text-lg">New document</DialogTitle></DialogHeader>
        {errorMsg && (
          <div data-testid="doc-create-error" className="rounded-xl border-2 border-red-500 bg-red-50 px-3 py-2 text-[12px] text-red-800 flex items-start gap-2">
            <X className="w-4 h-4 flex-shrink-0 mt-0.5 text-red-600" />
            <div><b className="font-mono uppercase tracking-wider text-[10px] text-red-700 block">Could not create document</b>{errorMsg}</div>
          </div>
        )}
        <div className="space-y-3">
          <Field label="Doc ID (optional — type anything you want, or leave blank to auto-assign)">
            <input data-testid="doc-id-hint" value={form.doc_id_hint} onChange={(e) => setForm({ ...form, doc_id_hint: e.target.value })}
              placeholder="e.g. SOP-0042, WI-Line3-2025, anything — or blank"
              className={inp} />
          </Field>
          <Field label="Doc Title *"><input data-testid="doc-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inp} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Pillar / Dept"><select data-testid="doc-pillar" value={form.pillar} onChange={(e) => setForm({ ...form, pillar: e.target.value })} className={inp}>
              {(pillars || []).map((p) => <option key={p} value={p}>{p}</option>)}
            </select></Field>
            <Field label="Code prefix (used only if Doc ID above is blank)"><select data-testid="doc-prefix" value={form.code_prefix} onChange={(e) => setForm({ ...form, code_prefix: e.target.value })} className={inp}>
              {(codePrefixes || []).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Initial rev"><input value={form.initial_rev} onChange={(e) => setForm({ ...form, initial_rev: e.target.value })} className={inp} /></Field>
            <Field label="Review cycle (days)"><input type="number" value={form.review_cycle_days} onChange={(e) => setForm({ ...form, review_cycle_days: Number(e.target.value) })} className={inp} /></Field>
          </div>
          <Field label="Attach initial document (any file type)">
            <PhotoUploader photoIds={fileIds} onChange={setFileIds} testIdPrefix="doc-file" accept="*/*" hideCamera />
          </Field>
          <Field label="Change note / summary * (required — logged in audit changelog)">
            <textarea data-testid="doc-summary" rows={2} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} className={inp} placeholder="Why is this document being created? What does the initial version contain?" />
          </Field>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="btn-pill btn-pill-secondary text-[11px]">Cancel</button>
          <button data-testid="doc-create-submit" onClick={submit} disabled={busy} className="btn-pill btn-pill-primary text-[11px]">
            {busy ? "Saving…" : "Create"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══ Document Editor Dialog (edit + upload new version + absorb + view versions)
function DocumentEditorDialog({ doc, role, employees = [], pillars, onClose, onSaved, allDocs }) {
  const [tab, setTab] = useState("edit"); // edit | versions | changelog
  const [form, setForm] = useState({});
  const [newFileIds, setNewFileIds] = useState([]);
  const [newRev, setNewRev] = useState("");
  const [newSummary, setNewSummary] = useState("");
  const [absorb, setAbsorb] = useState([]);
  const [versions, setVersions] = useState([]);
  const [changelog, setChangelog] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showDelete, setShowDelete] = useState(false);         // iter54
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleteReason, setDeleteReason] = useState("");

  useEffect(() => {
    if (doc) {
      setForm({
        code: doc.code || "",  // iter54 — editable Doc ID
        title: doc.title, pillar: doc.pillar, owner_id: doc.owner_id,
        review_cycle_days: doc.review_cycle_days, hub_ids: doc.hub_ids || [],
        raci_r_id: doc.raci_r_id || doc.owner_id || "",
        raci_a_id: doc.raci_a_id || "",
        raci_c_ids: doc.raci_c_ids || [],
        raci_i_ids: doc.raci_i_ids || [],
      });
      setNewFileIds([]); setNewRev(""); setNewSummary(""); setAbsorb([]); setTab("edit");
      api.get(`/documents/${doc.id}/versions`).then((r) => setVersions(r.data)).catch(() => {});
      api.get(`/documents/${doc.id}/changelog`).then((r) => setChangelog(r.data)).catch(() => {});
    }
  }, [doc?.id]);  // eslint-disable-line

  if (!doc) return null;

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...form };
      if (newFileIds[0]) {
        body.new_version_file_id = newFileIds[0];
        body.new_version_summary = newSummary;
        if (newRev.trim()) body.new_version_rev = newRev.trim();
      }
      if (absorb.length) body.absorbs_document_ids = absorb;
      const r = await api.patch(`/documents/${doc.id}`, body);
      const hasPendingEdit = r?.data?._pending_edit_id;
      const hasPendingVer = r?.data?._pending_version_id;
      if (hasPendingEdit && hasPendingVer) {
        toast.success("Saved — edit + new version queued in Approvals tab.");
      } else if (hasPendingEdit) {
        toast.success("Saved — changes queued in the Approvals tab (self-approve there to apply).");
      } else if (hasPendingVer) {
        toast.success("Saved — new version pending approval in the Approvals tab.");
      } else {
        toast.success("Saved.");
      }
      onSaved?.(); onClose?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const markReviewed = async () => {
    await api.patch(`/documents/${doc.id}`, { marked_reviewed: true });
    toast.success("Marked as reviewed"); onSaved?.(); onClose?.();
  };

  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const token = localStorage.getItem("opscmd_token") || "";
  const dl = (fid) => `${backend}/api/files/${fid}?auth=${encodeURIComponent(token)}`;

  const toggleMulti = (key, id) => {
    const cur = form[key] || [];
    setForm({ ...form, [key]: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] });
  };

  return (
    <Dialog open={!!doc} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-3xl rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500] font-bold">// {doc.code}</div>
          <DialogTitle className="font-display font-black text-lg flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#ff4500]" />
            {doc.title}
            <span className="text-[10px] font-mono ml-2">rev {doc.current_rev || "—"}</span>
            <StatusBadge status={doc.status} />
          </DialogTitle>
        </DialogHeader>

        <div className="flex gap-1 bg-[#f7f5f2] rounded-full p-1 w-fit">
          {[["edit","Edit / RACI / new version",Edit3],["versions","Versions",GitBranch],["changelog","Changelog",ClipboardList]].map(([k,l,Icon]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold flex items-center gap-1 ${
                tab === k ? "bg-[#1a1a1a] text-white" : "text-[#1a1a1a]/60"
              }`}>
              <Icon className="w-3 h-3" /> {l}
            </button>
          ))}
        </div>

        {tab === "edit" && (
          <div className="space-y-3">
            <div className="grid grid-cols-[140px_1fr] gap-3">
              <Field label="Doc ID">
                <input
                  data-testid="doc-edit-code"
                  value={form.code || ""}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  className={inp + " font-mono font-bold"}
                  placeholder="e.g. SOP-0042"
                />
              </Field>
              <Field label="Title"><input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inp} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Pillar / Dept"><select value={form.pillar} onChange={(e) => setForm({ ...form, pillar: e.target.value })} className={inp}>
                {(pillars || []).map((p) => <option key={p} value={p}>{p}</option>)}
              </select></Field>
              <Field label="Review cycle (days)">
                <input type="number" value={form.review_cycle_days || 365} onChange={(e) => setForm({ ...form, review_cycle_days: Number(e.target.value) })} className={inp} />
              </Field>
            </div>

            {/* RACI ─ inline on the document */}
            <div className="border-t border-[#1a1a1a]/06 pt-3">
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#ff4500] font-bold mb-2">// RACI · who owns what</div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="R · Responsible (single)">
                  <select data-testid="doc-raci-r" value={form.raci_r_id || ""} onChange={(e) => setForm({ ...form, raci_r_id: e.target.value })} className={inp}>
                    <option value="">— none —</option>
                    {employees.map((e) => <option key={e.id} value={e.id}>{e.name}{e.position ? ` · ${e.position}` : ""}</option>)}
                  </select>
                </Field>
                <Field label="A · Accountable (single)">
                  <select data-testid="doc-raci-a" value={form.raci_a_id || ""} onChange={(e) => setForm({ ...form, raci_a_id: e.target.value })} className={inp}>
                    <option value="">— none —</option>
                    {employees.map((e) => <option key={e.id} value={e.id}>{e.name}{e.position ? ` · ${e.position}` : ""}</option>)}
                  </select>
                </Field>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                <Field label={`C · Consulted (multi · ${(form.raci_c_ids || []).length} picked)`}>
                  <MultiPicker items={employees} value={form.raci_c_ids || []} onToggle={(id) => toggleMulti("raci_c_ids", id)} testId="doc-raci-c" />
                </Field>
                <Field label={`I · Informed (multi · ${(form.raci_i_ids || []).length} picked)`}>
                  <MultiPicker items={employees} value={form.raci_i_ids || []} onToggle={(id) => toggleMulti("raci_i_ids", id)} testId="doc-raci-i" />
                </Field>
              </div>
            </div>

            <div className="border-t border-[#1a1a1a]/06 pt-3">
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#ff4500] font-bold mb-2">// Upload new revision</div>
              <Field label={`Upload a new version (any file type — creates pending revision · needs Controller approval)`}>
                <PhotoUploader photoIds={newFileIds} onChange={setNewFileIds} testIdPrefix="doc-newver-file" accept="*/*" hideCamera />
              </Field>
              {newFileIds[0] && (
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <Field label={`New rev (auto = ${_bumpPreview(doc.current_rev)})`}>
                    <input value={newRev} onChange={(e) => setNewRev(e.target.value)} placeholder={_bumpPreview(doc.current_rev)} className={inp} />
                  </Field>
                  <Field label="Change summary">
                    <input data-testid="doc-newver-summary" value={newSummary} onChange={(e) => setNewSummary(e.target.value)} placeholder="Updated PPE requirements for line 3…" className={inp} />
                  </Field>
                </div>
              )}
            </div>

            <div className="border-t border-[#1a1a1a]/06 pt-3">
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#ff4500] font-bold mb-2">// Absorb / merge other documents (they get retired)</div>
              <select multiple value={absorb} onChange={(e) => setAbsorb([...e.target.selectedOptions].map((o) => o.value))}
                className={inp + " min-h-[80px]"}>
                {(allDocs || []).filter((d) => d.id !== doc.id && d.status !== "absorbed").map((d) => (
                  <option key={d.id} value={d.id}>{d.code} — {d.title}</option>
                ))}
              </select>
            </div>

            <div className="flex justify-between pt-2 flex-wrap gap-2">
              <div className="flex gap-2">
                <button onClick={markReviewed} className="btn-pill btn-pill-secondary text-[11px]" data-testid="doc-mark-reviewed">
                  <FileCheck2 className="w-3 h-3" /> Mark reviewed (no change)
                </button>
                {role === "controller" && !doc.is_deleted && (
                  <button onClick={() => setShowDelete(true)} className="btn-pill text-[11px] border border-red-500 text-red-600 hover:bg-red-50" data-testid="doc-delete-btn">
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={onClose} className="btn-pill btn-pill-secondary text-[11px]">Cancel</button>
                <button data-testid="doc-save-submit" onClick={save} disabled={busy} className="btn-pill btn-pill-primary text-[11px]">
                  {busy ? "Saving…" : "Save changes"}
                </button>
              </div>
            </div>
          </div>
        )}

        {tab === "versions" && (
          <ul className="space-y-2">
            {versions.length === 0 && <li className="text-[12px] text-[#1a1a1a]/55">No versions yet.</li>}
            {versions.map((v) => (
              <li key={v.id} className="border border-[#1a1a1a]/08 rounded-xl p-3 bg-white flex items-start justify-between gap-3">
                <div>
                  <div className="font-display font-bold text-sm">rev {v.rev} <StatusBadge status={v.status} /></div>
                  <div className="text-[11px] text-[#1a1a1a]/60 mt-0.5">{v.summary || "(no summary)"}</div>
                  <div className="text-[10px] font-mono text-[#1a1a1a]/40 mt-1 uppercase tracking-wider">
                    By {v.created_by_name} · {formatDate(v.created_at)}
                    {v.approved_by_name && ` · approved by ${v.approved_by_name} ${formatDate(v.approved_at)}`}
                  </div>
                </div>
                <a href={dl(v.file_id)} target="_blank" rel="noreferrer"
                  className="btn-pill btn-pill-secondary text-[10px] flex-shrink-0">
                  <ExternalLink className="w-3 h-3" /> Open
                </a>
              </li>
            ))}
          </ul>
        )}

        {tab === "changelog" && (
          <ChangeLogList entries={changelog} />
        )}

        {/* iter54 — Delete confirmation (type "delete" to confirm, logged with reason) */}
        {showDelete && (
          <Dialog open onOpenChange={(o) => !o && setShowDelete(false)}>
            <DialogContent className="max-w-md rounded-2xl">
              <DialogHeader>
                <DialogTitle className="font-display font-black text-lg text-red-600 flex items-center gap-2">
                  <Trash2 className="w-5 h-5" /> Delete {doc.code}?
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-[13px]">
                <p>This soft-deletes <b>{doc.code} — {doc.title}</b>. All history is preserved and logged in the changelog.</p>
                <Field label='Type "delete" to confirm'>
                  <input
                    data-testid="doc-delete-confirm"
                    autoFocus
                    value={deleteConfirm}
                    onChange={(e) => setDeleteConfirm(e.target.value)}
                    className={inp + " font-mono"}
                    placeholder="delete"
                  />
                </Field>
                <Field label="Reason (recorded in the changelog)">
                  <textarea
                    data-testid="doc-delete-reason"
                    rows={2}
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                    className={inp}
                    placeholder="Superseded by SOP-0042, obsolete, duplicate…"
                  />
                </Field>
              </div>
              <DialogFooter>
                <button onClick={() => { setShowDelete(false); setDeleteConfirm(""); setDeleteReason(""); }} className="btn-pill btn-pill-secondary text-[11px]">Cancel</button>
                <button
                  data-testid="doc-delete-confirm-submit"
                  disabled={deleteConfirm.trim().toLowerCase() !== "delete"}
                  onClick={async () => {
                    try {
                      await api.delete(`/documents/${doc.id}`, { data: { confirm: deleteConfirm, reason: deleteReason } });
                      toast.success(`${doc.code} deleted · logged in changelog`);
                      setShowDelete(false);
                      onSaved?.(); onClose?.();
                    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
                  }}
                  className="btn-pill text-[11px] bg-red-600 text-white hover:bg-red-700 disabled:opacity-40"
                >
                  <Trash2 className="w-3 h-3" /> Confirm delete
                </button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Multi-select checkbox picker used for RACI Consulted/Informed columns
function MultiPicker({ items, value, onToggle, testId }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const filtered = search
    ? items.filter((e) => (e.name + " " + (e.position || "")).toLowerCase().includes(search.toLowerCase()))
    : items;
  const selected = items.filter((e) => value.includes(e.id));
  return (
    <div className="relative" data-testid={testId}>
      <button type="button" onClick={() => setOpen(!open)} className={inp + " text-left flex items-center gap-2"}>
        <span className="flex-1 text-[11px] truncate">
          {selected.length === 0 ? <span className="text-[#1a1a1a]/40">— pick one or more —</span> :
            selected.slice(0, 3).map((e) => e.name).join(", ") + (selected.length > 3 ? ` +${selected.length - 3}` : "")}
        </span>
        <ChevronDown className="w-3 h-3 text-[#1a1a1a]/50" />
      </button>
      {open && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-[#1a1a1a]/15 rounded-xl shadow-lg max-h-64 overflow-hidden flex flex-col">
          <input placeholder="Filter…" value={search} onChange={(e) => setSearch(e.target.value)}
            className="border-b border-[#1a1a1a]/10 px-3 py-2 text-[11px] outline-none" />
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 && <div className="p-3 text-[11px] text-[#1a1a1a]/50">No employees.</div>}
            {filtered.map((e) => (
              <label key={e.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-[#f7f5f2] cursor-pointer">
                <input type="checkbox" checked={value.includes(e.id)} onChange={() => onToggle(e.id)} data-testid={`${testId}-opt-${e.id}`} />
                <span className="text-[12px]">{e.name}</span>
                {e.position && <span className="text-[10px] text-[#1a1a1a]/50 font-mono">· {e.position}</span>}
              </label>
            ))}
          </div>
          <div className="border-t border-[#1a1a1a]/10 flex justify-end p-1">
            <button type="button" onClick={() => setOpen(false)} className="text-[10px] uppercase tracking-wider font-bold text-[#ff4500] px-2">Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

function _bumpPreview(rev) {
  if (!rev) return "A";
  if (rev.length && /\d/.test(rev)) return String(Number(rev) + 1);
  if (rev === "Z") return "AA";
  return rev.slice(0, -1) + String.fromCharCode(rev.charCodeAt(rev.length - 1) + 1);
}

// ═══════════════════════════════════════════════════════════════════════
// APPROVALS TAB (Controller only)
// ═══════════════════════════════════════════════════════════════════════
function ApprovalsTab() {
  const [data, setData] = useState({ pending_versions: [], pending_edits: [], total: 0 });
  const load = () => api.get("/documents/approvals/queue")
    .then((r) => setData({
      pending_versions: r.data?.pending_versions || [],
      pending_edits: r.data?.pending_edits || [],
      total: r.data?.total || 0,
    }))
    .catch(() => {});
  useEffect(() => { load(); }, []);

  const approveVersion = async (docId) => {
    try {
      await api.post(`/documents/${docId}/approve`);
      toast.success("Version approved — hubs notified.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const approveEdit = async (editId) => {
    try {
      const r = await api.post(`/documents/pending-edits/${editId}/approve`);
      toast.success(r.data?.self_approved
        ? "Approved (self-approval recorded in changelog)."
        : "Approved.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const rejectEdit = async (editId) => {
    const reason = window.prompt("Reject reason (optional):", "");
    if (reason === null) return;
    try {
      await api.post(`/documents/pending-edits/${editId}/reject`, { reason });
      toast.success("Rejected.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const describeChange = (field, delta) => {
    if (field === "code") return `Doc ID: ${delta.from || "—"} → ${delta.to}`;
    if (field === "title") return `Title: "${delta.from || "—"}" → "${delta.to}"`;
    if (field === "pillar") return `Pillar: ${delta.from || "—"} → ${delta.to}`;
    if (field === "raci_r_id") return `R (Responsible) changed`;
    if (field === "raci_a_id") return `A (Accountable) changed`;
    if (field === "raci_c_ids") return `C (Consulted): ${(delta.from || []).length} → ${(delta.to || []).length} people`;
    if (field === "raci_i_ids") return `I (Informed): ${(delta.from || []).length} → ${(delta.to || []).length} people`;
    if (field === "review_cycle_days") return `Review cycle: ${delta.from}d → ${delta.to}d`;
    if (field === "hub_ids") return `Hubs: ${(delta.from || []).length} → ${(delta.to || []).length}`;
    if (field === "owner_id") return `Owner changed`;
    return `${field} changed`;
  };

  return (
    <div className="space-y-3">
      <div className="surface-card rounded-2xl p-4" data-testid="sop-approvals">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500] mb-2">
          // Pending Approvals · {data.total}
        </div>
        <div className="text-[11px] text-[#1a1a1a]/65 mb-3">
          Every doc creation, metadata edit, and file version routes through here — even edits made by Controllers. Self-approval is allowed and always recorded in the changelog.
        </div>

        {data.total === 0 ? (
          <div className="text-[12px] text-[#1a1a1a]/55 py-6 text-center font-mono uppercase tracking-wider">Nothing to approve.</div>
        ) : (
          <>
            {data.pending_versions.length > 0 && (
              <div className="mb-4">
                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#1a1a1a]/55 mb-2">File version uploads · {data.pending_versions.length}</div>
                <ul className="divide-y divide-[#1a1a1a]/06">
                  {data.pending_versions.map((d) => (
                    <li key={d.id} className="py-3 flex items-start justify-between gap-3" data-testid={`approve-row-${d.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-[#ff4500]">{d.code}</span>
                          <span className="font-display font-bold text-sm truncate">{d.title}</span>
                        </div>
                        <div className="text-[11px] text-[#1a1a1a]/65 mt-1">
                          {d.pending_version?.summary || "(no change summary)"}
                        </div>
                        <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/50 mt-1">
                          Proposed rev {d.pending_version?.rev} · by {d.pending_version?.created_by_name} · {formatDate(d.pending_version?.created_at)}
                        </div>
                      </div>
                      <button data-testid={`approve-btn-${d.id}`} onClick={() => approveVersion(d.id)} className="btn-pill btn-pill-primary text-[11px]">
                        <ShieldCheck className="w-3 h-3" /> Approve rev
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {data.pending_edits.length > 0 && (
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#1a1a1a]/55 mb-2">
                  Metadata edits & doc creations · {data.pending_edits.length}
                </div>
                <ul className="divide-y divide-[#1a1a1a]/06">
                  {data.pending_edits.map((e) => (
                    <li key={e.id} className="py-3 flex items-start justify-between gap-3" data-testid={`approve-edit-${e.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono font-bold text-[#ff4500]">{e.doc_code}</span>
                          <span className="font-display font-bold text-sm truncate">{e.doc_title}</span>
                          {e.is_creation && (
                            <span className="inline-block bg-[#e8f5ee] text-[#1a8754] font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-bold">Creation</span>
                          )}
                        </div>
                        <div className="text-[11px] text-[#1a1a1a]/75 mt-1 space-y-0.5">
                          {e.is_creation && Object.keys(e.changes || {}).length === 0 ? (
                            <div className="italic text-[#1a1a1a]/55">New document — awaiting first approval.</div>
                          ) : Object.entries(e.changes || {}).map(([f, d]) => (
                            <div key={f}>· {describeChange(f, d)}</div>
                          ))}
                          {(e.absorbs_document_ids || []).length > 0 && (
                            <div>· Absorbs {e.absorbs_document_ids.length} other doc(s)</div>
                          )}
                        </div>
                        <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/50 mt-1">
                          by {e.created_by_name} · {formatDate(e.created_at)}
                        </div>
                      </div>
                      <div className="flex flex-col gap-1">
                        <button data-testid={`approve-edit-btn-${e.id}`} onClick={() => approveEdit(e.id)} className="btn-pill btn-pill-primary text-[11px]">
                          <ShieldCheck className="w-3 h-3" /> Approve
                        </button>
                        <button data-testid={`reject-edit-btn-${e.id}`} onClick={() => rejectEdit(e.id)} className="btn-pill btn-pill-secondary text-[10px]">
                          Reject
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// (iter50) RaciTab + RaciLegend removed — RACI now inline on Doc Library.
// ═══════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════
// CHANGELOG TAB
// ═══════════════════════════════════════════════════════════════════════

// iter55 — Deleted Docs Bin (Controller-only). 30-day soft-delete recovery window.
function DeletedBinTab({ role }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoreReason, setRestoreReason] = useState("");
  const [restoreBusy, setRestoreBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/documents/deleted")
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const restore = async () => {
    if (!restoreTarget) return;
    setRestoreBusy(true);
    try {
      await api.post(`/documents/${restoreTarget.id}/restore`, { reason: restoreReason });
      toast.success(`${restoreTarget.code} restored to Library`);
      setRestoreTarget(null); setRestoreReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally {
      setRestoreBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="bg-amber-50 border border-amber-300 rounded-xl px-4 py-3 text-[12px] text-amber-900">
        <b>30-day recovery window.</b> Soft-deleted documents (all versions, RACI, changelog preserved) can be restored here for 30 days after deletion. After that, they are marked eligible for permanent purge. {role === "controller" ? "Click Restore to bring a document back to the Library." : "Only Controllers can restore documents."}
      </div>

      {loading ? (
        <div className="surface-card rounded-2xl p-6 text-center text-[12px] text-[#1a1a1a]/50">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="surface-card rounded-2xl p-8 text-center text-[12px] text-[#1a1a1a]/55 font-mono uppercase tracking-wider" data-testid="deleted-bin-empty">
          Nothing deleted. Your Doc Library is intact.
        </div>
      ) : (
        <div className="surface-card rounded-2xl overflow-x-auto" data-testid="deleted-bin-table">
          <table className="w-full text-[12px]">
            <thead className="bg-[#1a1a1a] text-white">
              <tr>
                <th className="text-left px-3 py-2 text-[9px] uppercase tracking-wider font-mono font-bold w-[110px]">Doc ID</th>
                <th className="text-left px-3 py-2 text-[9px] uppercase tracking-wider font-mono font-bold min-w-[220px]">Title</th>
                <th className="text-left px-3 py-2 text-[9px] uppercase tracking-wider font-mono font-bold w-[110px]">Pillar</th>
                <th className="text-left px-3 py-2 text-[9px] uppercase tracking-wider font-mono font-bold w-[150px]">Deleted by</th>
                <th className="text-left px-3 py-2 text-[9px] uppercase tracking-wider font-mono font-bold w-[110px]">Deleted at</th>
                <th className="text-left px-3 py-2 text-[9px] uppercase tracking-wider font-mono font-bold min-w-[200px]">Reason</th>
                <th className="text-left px-3 py-2 text-[9px] uppercase tracking-wider font-mono font-bold w-[130px]">Purge in</th>
                <th className="text-left px-3 py-2 text-[9px] uppercase tracking-wider font-mono font-bold w-[110px]"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1a1a1a]/06">
              {rows.map((d) => (
                <tr key={d.id} data-testid={`deleted-row-${d.id}`} className="hover:bg-[#f7f5f2]/50 align-top">
                  <td className="px-3 py-2.5 font-mono font-bold text-[#1a1a1a]/70">{d.code}</td>
                  <td className="px-3 py-2.5 font-medium text-[#1a1a1a]/70">{d.title}</td>
                  <td className="px-3 py-2.5 font-mono text-[10px] uppercase tracking-wider text-[#1a1a1a]/60">{d.pillar}</td>
                  <td className="px-3 py-2.5 text-[12px] text-[#1a1a1a]/70">{d.deleted_by_name || "—"}</td>
                  <td className="px-3 py-2.5 font-mono text-[10px] text-[#1a1a1a]/60">{d.deleted_at ? formatDate(d.deleted_at) : "—"}</td>
                  <td className="px-3 py-2.5 text-[11px] text-[#1a1a1a]/65 italic">{d.deletion_reason || "(no reason recorded)"}</td>
                  <td className="px-3 py-2.5">
                    {d.days_until_purge != null ? (
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-mono font-bold uppercase tracking-wider ${
                        d.days_until_purge === 0 ? "bg-red-100 text-red-700" :
                        d.days_until_purge <= 7 ? "bg-amber-100 text-amber-800" :
                        "bg-emerald-100 text-emerald-800"
                      }`}>
                        {d.days_until_purge === 0 ? "eligible" : `${d.days_until_purge}d left`}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2.5">
                    {role === "controller" && (
                      <button
                        onClick={() => { setRestoreTarget(d); setRestoreReason(""); }}
                        data-testid={`deleted-restore-${d.id}`}
                        className="btn-pill text-[10px] border border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                      >
                        <FileCheck2 className="w-3 h-3" /> Restore
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Restore confirmation dialog */}
      <Dialog open={!!restoreTarget} onOpenChange={(o) => !o && setRestoreTarget(null)}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-lg text-emerald-700 flex items-center gap-2">
              <FileCheck2 className="w-5 h-5" /> Restore {restoreTarget?.code}?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-[13px]">
            <p>Bring <b>{restoreTarget?.code} — {restoreTarget?.title}</b> back to the active Library. All versions, RACI settings, and changelog are already preserved.</p>
            <Field label="Reason (recorded in the changelog, optional)">
              <textarea
                data-testid="deleted-restore-reason"
                rows={2}
                value={restoreReason}
                onChange={(e) => setRestoreReason(e.target.value)}
                className={inp}
                placeholder="Deleted by accident, wrong doc, etc."
              />
            </Field>
          </div>
          <DialogFooter>
            <button onClick={() => setRestoreTarget(null)} className="btn-pill btn-pill-secondary text-[11px]">Cancel</button>
            <button
              data-testid="deleted-restore-submit"
              disabled={restoreBusy}
              onClick={restore}
              className="btn-pill text-[11px] bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40"
            >
              <FileCheck2 className="w-3 h-3" /> Confirm restore
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function ChangelogTab() {
  const [entries, setEntries] = useState([]);
  const [filter, setFilter] = useState({ date_from: "", date_to: "", document_id: "", kind: "" });
  const [docs, setDocs] = useState([]);

  const load = () => {
    const params = {};
    if (filter.date_from) params.date_from = filter.date_from;
    if (filter.date_to) params.date_to = filter.date_to;
    if (filter.document_id) params.document_id = filter.document_id;
    if (filter.kind) params.kind = filter.kind;
    api.get("/changelog", { params }).then((r) => setEntries(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line
  useEffect(() => { api.get("/documents").then((r) => setDocs(r.data)).catch(() => {}); }, []);

  return (
    <div className="space-y-3" data-testid="changelog-tab">
      <div className="flex flex-wrap gap-2 items-center">
        <Filter className="w-3 h-3 text-[#1a1a1a]/50" />
        <input type="date" value={filter.date_from} onChange={(e) => setFilter({ ...filter, date_from: e.target.value })}
          className="border border-[#1a1a1a]/15 rounded px-2 py-1 text-[11px] bg-white" placeholder="from" />
        <span className="text-[11px] text-[#1a1a1a]/50">→</span>
        <input type="date" value={filter.date_to} onChange={(e) => setFilter({ ...filter, date_to: e.target.value })}
          className="border border-[#1a1a1a]/15 rounded px-2 py-1 text-[11px] bg-white" placeholder="to" />
        <select value={filter.document_id} onChange={(e) => setFilter({ ...filter, document_id: e.target.value })}
          className="border border-[#1a1a1a]/15 rounded px-2 py-1 text-[11px] bg-white">
          <option value="">All documents</option>
          {docs.map((d) => <option key={d.id} value={d.id}>{d.code} — {d.title}</option>)}
        </select>
        <select value={filter.kind} onChange={(e) => setFilter({ ...filter, kind: e.target.value })}
          className="border border-[#1a1a1a]/15 rounded px-2 py-1 text-[11px] bg-white">
          <option value="">All events</option>
          <option value="create">Created</option>
          <option value="edit">Edited</option>
          <option value="approved">Approved</option>
          <option value="absorb">Absorbed</option>
          <option value="deleted">Deleted</option>
          <option value="raci_updated">RACI updated</option>
        </select>
        <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/50 ml-auto">
          {entries.length} entries
        </div>
      </div>
      <ChangeLogList entries={entries} />
    </div>
  );
}

function ChangeLogList({ entries }) {
  if (entries.length === 0) return <div className="text-[12px] text-[#1a1a1a]/55 py-8 text-center">No entries.</div>;
  return (
    <ul className="space-y-1" data-testid="changelog-list">
      {entries.map((e) => (
        <li key={e.id} className="border border-[#1a1a1a]/06 rounded-lg px-3 py-2 bg-white text-[12px] flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-[#1a1a1a] text-white">
                {(e.kind || "").replace("_", " ")}
              </span>
              {e.document_code && (
                <span className="font-mono font-bold text-[11px] text-[#ff4500]">{e.document_code}</span>
              )}
              <span className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">
                {e.actor_name} · {formatDate(e.created_at)}
              </span>
            </div>
            {e.details && Object.keys(e.details).length > 0 && (
              <pre className="text-[10px] font-mono text-[#1a1a1a]/60 mt-1 whitespace-pre-wrap">
                {JSON.stringify(e.details, null, 0).slice(0, 300)}
              </pre>
            )}
          </div>
          <div className="text-[9px] font-mono uppercase tracking-wider text-[#1a1a1a]/40 text-right">
            Sig hash<br />
            {e.actor_signature?.slice(-16) || "—"}
          </div>
        </li>
      ))}
    </ul>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.18em] font-mono text-[#1a1a1a]/65 mb-1">{label}</label>
      {children}
    </div>
  );
}

const inp = "w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#1a1a1a] focus:ring-2 focus:ring-[#1a1a1a]/15";
